import React, { useState } from 'react';
import { ButtonProps } from '../../types';
import Button from '../../components/atoms/Button';
import Input from '../../components/atoms/Input';
import GoogleSignInButton from '../../components/atoms/GoogleSignInButton';
import './AdminLogin.css';

interface AdminLoginProps {
  onLogin: (email: string, password: string) => void;
  onGoogleSignIn: () => void;
  onGoToSignup: () => void;
  isLoading?: boolean;
  error?: string;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLogin, onGoogleSignIn, onGoToSignup, isLoading = false, error }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formErrors, setFormErrors] = useState<{ email?: string; password?: string }>({});

  const validateForm = () => {
    const errors: { email?: string; password?: string } = {};

    if (!email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onLogin(email, password);
    }
  };

  return (
    <div className="admin-login">
      <div className="admin-login-container">
        <div className="admin-login-header">
          <a href="/" className="admin-logo-link" aria-label="Go to homepage">
            <img src="/images/logo/tfr.avif" alt="The Frosting Room" className="admin-logo" />
          </a>
          <h1>Admin Login</h1>
          <p>Welcome back! Please sign in to your account.</p>
        </div>

        <form onSubmit={handleSubmit} className="admin-login-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <Input
              type="email"
              id="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={formErrors.email ? 'error' : ''}
              required
            />
            {formErrors.email && (
              <span className="field-error">{formErrors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <Input
              type="password"
              id="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={formErrors.password ? 'error' : ''}
              required
            />
            {formErrors.password && (
              <span className="field-error">{formErrors.password}</span>
            )}
          </div>

          <div className="form-actions">
            <Button
              type="submit"
              variant="primary"
              size="large"
              disabled={isLoading}
              className="login-button"
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>
          </div>

          <div className="divider">
            <span>or</span>
          </div>

          <div className="google-signin-section">
            <GoogleSignInButton
              onClick={onGoogleSignIn}
              isLoading={isLoading}
              disabled={isLoading}
            />
          </div>
        </form>

        <div className="admin-login-footer">
          <p>Don't have an account? 
            <button 
              type="button" 
              onClick={onGoToSignup}
              className="signup-link"
            >
              Sign up here
            </button>
          </p>
          <p className="copyright">© 2024 The Frosting Room. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
